import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import { ApiService, URLS, ZITI_URLS } from '@netfoundry-ui/shared/services';
import { ElasticsearchService } from '@netfoundry-ui/shared/elasticsearch';
import { TEMPLATE_SEARCH_SERVICE, TemplateSearchService } from '@netfoundry-ui/shared/apiv2';
import type { TrafficRow } from '@netfoundry-ui/shared/model';
import {
    assembleMicrosegments,
    collapsePortRanges,
    CreationStatus,
    HostSpec,
    MICROSEGMENT_SOURCE_TAG_KEY,
    MICROSEGMENT_SOURCE_TRAFFIC,
    MicrosegmentDraft,
    MicrosegmentExistingIds,
    microsegmentToTrafficCard,
    parseTrafficFlows,
    SimpleServiceCreateDraft,
    SimpleServiceCreateService,
    suggestServiceName,
} from '@netfoundry-ui/shared/service-templates';
import { ServicesPageService, ValidationService, ZITI_DATA_SERVICE, ZitiDataService } from 'ziti-console-lib';
import { firstValueFrom, Subscription } from 'rxjs';
import moment from 'moment';
import { CoverageServiceInput, isFlowCovered, isSegmentCovered } from './coverage-sankey.adapter';
import { CoverageSankeyDialogComponent, CoverageDialogData } from './coverage-sankey-dialog.component';

/** How a side of a service is reached or hosted. */
type SideMode = 'address' | 'sdk';
/** The protocols a client dials, for the intercept. */
type AccessProtocol = 'tcp' | 'udp' | 'both';

/**
 * One service to be created from the selected traffic. The operator builds a list of these from the
 * observed pool, so a service can cover one segment or several, and some observed traffic can be left
 * out. Each carries its own name, attributes, accessing config and hosting config.
 */
interface ServiceConfig {
    name: string;
    attributes: string[]; // '#role' tokens
    // Accessing (intercept / dial).
    addresses: string[];
    ports: string[];
    accessMode: SideMode;
    accessProtocol: AccessProtocol;
    dialAttributes: string[]; // '@name' / '#role'
    // Hosting (host / bind). Each delivery dimension is pinned (fixed) or forwarded.
    hostMode: SideMode;
    fwdProtocol: boolean;
    protocolVal: 'tcp' | 'udp';
    fwdAddress: boolean;
    addressVal: string;
    fwdPort: boolean;
    portVal: string;
    allowedAddresses: string[];
    allowedPorts: string[];
    allowedProtocols: string[];
    // Source IPs/CIDRs allowed to reach the host (empty = any). The field suggests the observed sources.
    allowedSourceAddresses: string[];
    moreOpen: boolean;
    bindAttributes: string[]; // '@name' / '#role'
    // Whether the operator set a host dimension by hand (so we stop auto-deriving its default).
    touchedAddr: boolean;
    touchedPort: boolean;
    // View state.
    open: boolean;
    // Briefly true right after the card is added, to play a one-shot highlight.
    justAdded?: boolean;
    morePopoverOpen: boolean;
    // Creation progress.
    statuses: CreationStatus[];
    state: 'idle' | 'creating' | 'done' | 'error';
}

/**
 * New-generation "Create services from traffic" page, routed at `services/create-from-traffic` under
 * the beta toggle. Reached from Traffic Analysis with the selection in the query string, it lets the
 * operator build one or more services from that pool - each its own card with its own accessing and
 * hosting config - and creates the services, configs and policies, showing progress. It reuses the
 * shared `SimpleServiceCreateService`, called once per service.
 */
@Component({
    selector: 'app-services-from-traffic',
    templateUrl: './services-from-traffic.component.html',
    styleUrls: ['./services-from-traffic.component.scss'],
    standalone: false,
})
export class ServicesFromTrafficComponent implements OnInit, OnDestroy {
    services: ServiceConfig[] = [];

    // Suggestion lists shared by every service's pickers.
    availableServiceRoleAttributes: string[] = [];
    availableIdentityRoleAttributes: string[] = [];
    availableIdentityNamedAttributes: string[] = [];

    fromTraffic = false;

    // Edit context: set when /services/create-from-traffic/:id carries a service id. The page opens with
    // one card pre-filled from that microsegment and saving patches it in place.
    editServiceId?: string;
    loadingEdit = false;
    editLoadError = '';
    private editExisting?: MicrosegmentExistingIds;

    // Creation progress (the whole run).
    started = false;
    creating = false;
    finished = false;
    hasErrors = false;

    private readonly subscription = new Subscription();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private growlerService: GrowlerService,
        private createService: SimpleServiceCreateService,
        private servicesPageService: ServicesPageService,
        private validationService: ValidationService,
        @Inject(ZITI_DATA_SERVICE) private ziti: ZitiDataService,
        private dialog: MatDialog,
        private apiService: ApiService,
        private elasticsearch: ElasticsearchService,
        @Inject(TEMPLATE_SEARCH_SERVICE) private templateService: TemplateSearchService
    ) {}

    ngOnInit(): void {
        this.loadAttributeLists();
        this.getIdentityNamedAttributes();
        // Editing an existing microsegment (/services/create-from-traffic/:id): pre-fill one card from it
        // and skip the traffic-selection seeding and coverage (there is no selection in edit mode).
        const editId = this.route.snapshot.paramMap.get('id');
        if (editId) {
            this.editServiceId = editId;
            this.loadForEdit(editId);
            return;
        }
        this.subscription.add(this.route.queryParamMap.subscribe(() => this.seedFromQuery()));
        // The path re-query needs the current network, which resolves after init - run it on the first
        // network that arrives (once), not synchronously here where it may still be null.
        this.subscription.add(
            this.apiService.currentNetwork.subscribe((net: any) => {
                if (!this.pathsRequested && net?.id && net?.networkGroupId) {
                    this.pathsRequested = true;
                    this.loadPathCoverage();
                }
            })
        );
    }

    get isEdit(): boolean {
        return !!this.editServiceId;
    }

    /** Fetch the microsegment behind the edit id, map it to one service card, and remember the ids to patch. */
    private async loadForEdit(id: string): Promise<void> {
        this.loadingEdit = true;
        this.editLoadError = '';
        const paging = { total: 2000, page: 1, sort: 'name', order: 'asc', searchOn: 'name', filter: '' };
        try {
            const [services, configs, policies, identities] = await Promise.all([
                this.ziti.get('services', paging, []),
                this.ziti.get('configs', paging, []),
                this.ziti.get('service-policies', paging, []),
                this.ziti.get('identities', paging, []),
            ]);
            const rowsOf = (r: any) => r?.data ?? r ?? [];
            const identityRows = rowsOf(identities);
            const idNameById: Record<string, string> = {};
            for (const row of identityRows) {
                const rid = `${row?.id ?? row?.data?.id ?? ''}`;
                const name = `${row?.name ?? row?.data?.name ?? ''}`;
                if (rid && name) {
                    idNameById[rid] = name;
                }
            }
            const segments = assembleMicrosegments({
                services: rowsOf(services),
                configs: rowsOf(configs),
                policies: rowsOf(policies),
                identities: identityRows,
            });
            const segment = segments.find((s) => s.serviceId === id);
            if (!segment) {
                this.editLoadError = 'This service could not be opened for editing as a microsegment.';
                return;
            }
            const seed = microsegmentToTrafficCard(segment, idNameById);
            this.editExisting = seed.existing;
            this.services = [this.cardFromSeed(seed)];
        } catch {
            this.editLoadError = 'Could not load this microsegment. Please try again.';
        } finally {
            this.loadingEdit = false;
        }
    }

    /** Build a full service card from a microsegment seed. Marks the host dimensions as touched so the
     *  auto-derive helpers don't overwrite the loaded delivery target. */
    private cardFromSeed(seed: ReturnType<typeof microsegmentToTrafficCard>): ServiceConfig {
        return {
            name: seed.name,
            attributes: seed.attributes,
            addresses: seed.addresses,
            ports: seed.ports,
            accessMode: seed.accessMode,
            accessProtocol: seed.accessProtocol,
            dialAttributes: seed.dialTokens,
            hostMode: seed.hostMode,
            fwdProtocol: seed.fwdProtocol,
            protocolVal: seed.protocolVal,
            fwdAddress: seed.fwdAddress,
            addressVal: seed.addressVal,
            fwdPort: seed.fwdPort,
            portVal: seed.portVal,
            allowedAddresses: seed.allowedAddresses,
            allowedPorts: seed.allowedPorts,
            allowedProtocols: seed.allowedProtocols,
            allowedSourceAddresses: seed.allowedSourceAddresses,
            moreOpen: false,
            bindAttributes: seed.bindTokens,
            touchedAddr: true,
            touchedPort: true,
            open: true,
            morePopoverOpen: false,
            statuses: [],
            state: 'idle',
        };
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /** A click anywhere closes any open from-selection picker or detail popover; triggers and the
     *  popovers themselves stop propagation so their own clicks do not close them. */
    @HostListener('document:click')
    closeAllPopovers(): void {
        this.services.forEach((s) => {
            s.morePopoverOpen = false;
        });
        this.popoverRow = null;
        this.covInfoOpen = false;
    }

    /** The "what is a path?" explainer popover beside the Coverage figure. */
    covInfoOpen = false;
    toggleCovInfo(event: Event): void {
        event.stopPropagation();
        this.covInfoOpen = !this.covInfoOpen;
    }

    // --- Suggestion lists -----------------------------------------------------

    private loadAttributeLists(): void {
        this.servicesPageService
            .getServiceRoleAttributes()
            .then((result: any) => (this.availableServiceRoleAttributes = result?.data ?? []))
            .catch(() => (this.availableServiceRoleAttributes = []));
        this.servicesPageService
            .getIdentityRoleAttributes()
            .then((result: any) => (this.availableIdentityRoleAttributes = result?.data ?? []))
            .catch(() => (this.availableIdentityRoleAttributes = []));
    }

    getIdentityNamedAttributes(filter?: string): void {
        const paging = { searchOn: 'name', filter: filter || '', total: 30, page: 1, sort: 'name', order: 'asc' };
        const filters = filter
            ? [{ filterName: 'name', columnId: 'name', value: filter, label: '', type: 'TEXTINPUT' }]
            : [];
        this.ziti
            .get('identities', paging, filters)
            .then((result: any) => {
                this.availableIdentityNamedAttributes = (result?.data ?? []).map((identity: any) => identity.name);
            })
            .catch(() => (this.availableIdentityNamedAttributes = []));
    }

    get serviceSuggestions(): string[] {
        return this.availableServiceRoleAttributes.map((r) => `#${r}`);
    }
    get accessSuggestionGroups(): { label: string; items: string[] }[] {
        return this.identitySuggestionGroups(this.selectedSourceIdentities);
    }
    get hostSuggestionGroups(): { label: string; items: string[] }[] {
        return this.identitySuggestionGroups(this.selectedHostIdentities);
    }
    /** The observed source IPs offered in the "Allowed source addresses" field's dropdown. */
    get sourceSuggestionGroups(): { label: string; items: string[] }[] {
        return this.selectedSourceIps.length ? [{ label: 'From selected traffic segments', items: this.selectedSourceIps }] : [];
    }

    private identitySuggestionGroups(selected: string[]): { label: string; items: string[] }[] {
        const chosen = new Set<string>();
        const fromSelection: string[] = [];
        selected.forEach((name) => {
            if (name && !chosen.has(name)) {
                chosen.add(name);
                fromSelection.push(`@${name}`);
            }
        });
        const roles = this.availableIdentityRoleAttributes.map((r) => `#${r}`);
        const identities = this.availableIdentityNamedAttributes.filter((n) => !chosen.has(n)).map((n) => `@${n}`);
        const groups: { label: string; items: string[] }[] = [];
        if (fromSelection.length) groups.push({ label: 'From selected traffic segments', items: fromSelection });
        if (roles.length) groups.push({ label: 'Roles', items: roles });
        if (identities.length) groups.push({ label: 'Identities', items: identities });
        return groups;
    }

    readonly attrTone = (token: string): 'green' | 'blue' => (token.startsWith('@') ? 'green' : 'blue');
    readonly normalizeAttrToken = (raw: string): string => {
        const value = (raw ?? '').trim();
        if (!value) return value;
        return value.startsWith('@') || value.startsWith('#') ? value : `#${value}`;
    };

    private namedOf(attrs: string[]): string[] {
        return attrs.filter((a) => a.startsWith('@')).map((a) => a.slice(1));
    }
    private rolesOf(attrs: string[]): string[] {
        return attrs.filter((a) => !a.startsWith('@')).map((a) => (a.startsWith('#') ? a.slice(1) : a));
    }

    // --- Seeding + the observed pool -----------------------------------------

    /** The observed address:port pairs from the Traffic Analysis selection (`destPairs`). */
    get observedPairs(): { address: string; port: string }[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('destPairs')).map((pair) => {
            const idx = pair.lastIndexOf(':');
            return { address: idx > 0 ? pair.slice(0, idx) : pair, port: idx > 0 ? pair.slice(idx + 1) : '' };
        });
    }
    get observedAddresses(): string[] {
        return this.distinct(this.observedPairs.map((p) => p.address).filter(Boolean));
    }
    get observedPorts(): string[] {
        return this.distinct(this.observedPairs.map((p) => p.port).filter(Boolean));
    }
    get selectedSourceIdentities(): string[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('srcIdentity'));
    }
    get selectedHostIdentities(): string[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('hostIdentity'));
    }
    /** The source IPs observed in the selection; "(empty)" is the placeholder for identity initiators. */
    get selectedSourceIps(): string[] {
        return this.splitParam(this.route.snapshot.queryParamMap.get('srcIp')).filter((ip) => ip !== '(empty)');
    }
    get sourceLabel(): string | undefined {
        return this.route.snapshot.queryParamMap.get('label') || undefined;
    }

    /** Access/host shape carried over from the preview step, used as the first service card's defaults. */
    private get seededAccessMode(): SideMode {
        return this.route.snapshot.queryParamMap.get('accessMode') === 'sdk' ? 'sdk' : 'address';
    }
    private get seededHostMode(): SideMode {
        return this.route.snapshot.queryParamMap.get('hostMode') === 'sdk' ? 'sdk' : 'address';
    }

    private seedFromQuery(): void {
        const pairs = this.observedPairs;
        this.fromTraffic = pairs.length > 0;
        this.started = false;
        this.creating = false;
        this.finished = false;
        this.hasErrors = false;
        // The chooser's 'custom' setup opens a single blank service - nothing from the selection pre-filled -
        // so the operator builds it by hand (the observed pool is still offered as suggestions).
        if (this.route.snapshot.queryParamMap.get('setup') === 'custom') {
            const blank = this.newService([], [], 0, this.seededAccessMode, this.seededHostMode);
            blank.dialAttributes = [];
            blank.bindAttributes = [];
            this.services = [blank];
            return;
        }
        // Recommended setup: one service per destination address (grouping that address's observed ports), so
        // a selection spanning several hosts becomes several services. The operator can still merge, trim, or
        // rename from there, and each multi-destination card gets a suggested, editable name.
        const byAddress = this.portsByDestAddress(pairs);
        const addresses = Array.from(byAddress.keys());
        this.services =
            addresses.length <= 1
                ? [this.newService(this.observedAddresses, this.observedPorts, 0, this.seededAccessMode, this.seededHostMode)]
                : addresses.map((address, i) => {
                      const ports = byAddress.get(address) ?? [];
                      const svc = this.newService([address], ports, i, this.seededAccessMode, this.seededHostMode);
                      svc.name = this.suggestServiceNameFor([address], ports);
                      return svc;
                  });
    }

    /** Group the observed destination pairs into address -> its distinct ports, preserving first-seen order. */
    private portsByDestAddress(pairs: { address: string; port: string }[]): Map<string, string[]> {
        const map = new Map<string, string[]>();
        for (const { address, port } of pairs) {
            if (!address) {
                continue;
            }
            const ports = map.get(address) ?? [];
            if (port && !ports.includes(port)) {
                ports.push(port);
            }
            map.set(address, ports);
        }
        return map;
    }

    /** A suggested, editable service name for one destination address and its ports. */
    private suggestServiceNameFor(addresses: string[], ports: string[]): string {
        const portNums = ports.map((p) => Number(p)).filter((n) => Number.isFinite(n));
        return suggestServiceName(addresses, collapsePortRanges(portNums));
    }

    private newService(
        addresses: string[],
        ports: string[],
        index: number,
        accessMode: SideMode = 'address',
        hostMode: SideMode = 'address'
    ): ServiceConfig {
        const dial = this.selectedSourceIdentities.map((n) => `@${n}`);
        const bind = this.selectedHostIdentities.map((n) => `@${n}`);
        return {
            name: '',
            attributes: [],
            addresses: [...addresses],
            ports: [...ports],
            accessMode,
            accessProtocol: 'tcp',
            dialAttributes: dial,
            hostMode,
            // Pin (fixed) a dimension when the service has exactly one value for it, forward when it spans
            // several (one fixed value cannot stand for a range).
            fwdProtocol: false,
            protocolVal: 'tcp',
            fwdAddress: addresses.length !== 1,
            addressVal: addresses[0] || '',
            fwdPort: ports.length !== 1,
            portVal: ports[0] || '',
            allowedAddresses: [],
            allowedPorts: [],
            allowedProtocols: [],
            allowedSourceAddresses: [],
            moreOpen: false,
            bindAttributes: bind,
            touchedAddr: false,
            touchedPort: false,
            open: index === 0,
            morePopoverOpen: false,
            statuses: [],
            state: 'idle',
        };
    }

    addService(): void {
        if (this.started) return;
        const created = this.newService([], [], this.services.length);
        // Collapse the rest and open the new card, keeping the accordion to a single open service.
        this.services.forEach((x) => (x.open = false));
        created.open = true;
        created.justAdded = true;
        this.services.push(created);
        setTimeout(() => (created.justAdded = false), 1200);
    }
    removeService(s: ServiceConfig): void {
        if (this.started) return;
        this.services = this.services.filter((x) => x !== s);
    }

    // --- Per-service edits ----------------------------------------------------

    /** Accordion: only one service is expanded at a time, so the list of cards stays compact and the
     *  "Add another service" action at the top never scrolls out of view behind a tall open form. */
    toggleOpen(s: ServiceConfig): void {
        const willOpen = !s.open;
        this.services.forEach((x) => (x.open = false));
        s.open = willOpen;
    }
    setAccessMode(s: ServiceConfig, mode: SideMode): void {
        if (!this.started) s.accessMode = mode;
    }
    setHostMode(s: ServiceConfig, mode: SideMode): void {
        if (!this.started) s.hostMode = mode;
    }
    setAccessProtocol(s: ServiceConfig, protocol: AccessProtocol): void {
        if (!this.started) s.accessProtocol = protocol;
    }
    setFwd(s: ServiceConfig, dim: 'Protocol' | 'Address' | 'Port', forward: boolean): void {
        if (this.started) return;
        if (dim === 'Protocol') s.fwdProtocol = forward;
        if (dim === 'Address') {
            s.fwdAddress = forward;
            s.touchedAddr = true;
        }
        if (dim === 'Port') {
            s.fwdPort = forward;
            s.touchedPort = true;
        }
    }
    toggleMore(s: ServiceConfig): void {
        if (!this.started) s.moreOpen = !s.moreOpen;
    }
    /** The host's allowed protocols are a closed set (tcp/udp), so each is an on/off pill; none selected
     *  means the dialed protocol is used. */
    toggleAllowedProtocol(s: ServiceConfig, protocol: 'tcp' | 'udp'): void {
        if (this.started) return;
        s.allowedProtocols = s.allowedProtocols.includes(protocol)
            ? s.allowedProtocols.filter((p) => p !== protocol)
            : [...s.allowedProtocols, protocol];
    }
    /** Drop every observed source address into this service's allowed-source list in one click (deliberate
     *  lockdown to the sources seen in the selection); merged with anything already there, no duplicates. */
    applyAllSourceAddresses(s: ServiceConfig): void {
        if (this.started) return;
        s.allowedSourceAddresses = Array.from(new Set([...s.allowedSourceAddresses, ...this.selectedSourceIps]));
    }
    /** True once every observed source address is already in this service's allowed-source list. */
    allSourceAddressesApplied(s: ServiceConfig): boolean {
        return this.selectedSourceIps.length > 0 && this.selectedSourceIps.every((ip) => s.allowedSourceAddresses.includes(ip));
    }

    /** Keep the fixed host defaults in step with the intercept until the operator sets them by hand. */
    onAddressesChange(s: ServiceConfig): void {
        if (!s.touchedAddr) {
            s.fwdAddress = s.addresses.length !== 1;
            if (!s.fwdAddress) s.addressVal = s.addresses[0] || '';
        }
    }
    onPortsChange(s: ServiceConfig): void {
        if (!s.touchedPort) {
            s.fwdPort = s.ports.length !== 1;
            if (!s.fwdPort) s.portVal = s.ports[0] || '';
        }
    }

    // --- Observed-value dropdowns --------------------------------------------

    /** Stop a click inside a popover (or on its trigger) from bubbling to the document/card handlers. */
    stop(ev: Event): void {
        ev.stopPropagation();
    }
    /** Observed addresses offered in the Addresses field's dropdown (opens on focus). */
    get observedAddressGroups(): { label: string; items: string[] }[] {
        return this.observedAddresses.length ? [{ label: 'From selected traffic segments', items: this.observedAddresses }] : [];
    }
    /** Observed ports offered in the Ports field's dropdown. */
    get observedPortGroups(): { label: string; items: string[] }[] {
        return this.observedPorts.length ? [{ label: 'From selected traffic segments', items: this.observedPorts }] : [];
    }

    // --- Summary (collapsed header) ------------------------------------------

    accessPrimary(s: ServiceConfig): string {
        if (s.accessMode === 'sdk') return 'SDK client';
        const addr = s.addresses[0] || '-';
        return `${addr}:${s.ports.join(',') || '-'}`;
    }
    accessMoreCount(s: ServiceConfig): number {
        return Math.max(0, s.addresses.length - 1);
    }
    toggleMorePopover(s: ServiceConfig, ev: Event): void {
        ev.stopPropagation();
        const open = !s.morePopoverOpen;
        this.closeAllPopovers();
        s.morePopoverOpen = open;
    }
    hostSummary(s: ServiceConfig): string {
        if (s.hostMode === 'sdk') return 'SDK';
        if (s.fwdAddress && s.fwdPort) return 'forwarded';
        const sameAddr = !s.fwdAddress && s.addressVal.trim() === (s.addresses[0] || '').trim();
        const samePort = s.fwdPort || s.portVal.trim() === (s.ports[0] || '').trim();
        if (sameAddr && samePort) return 'same host';
        const addr = s.fwdAddress ? 'dialed' : s.addressVal.trim() || '-';
        const port = s.fwdPort ? '' : `:${s.portVal.trim()}`;
        return `${addr}${port}`;
    }

    // --- Coverage + totals ----------------------------------------------------

    get coveredCount(): number {
        // Shares isSegmentCovered with the coverage diagram so the bar and the diagram can never disagree.
        return this.observedPairs.filter((p) => isSegmentCovered(p.address, p.port, this.services)).length;
    }
    get totalSegments(): number {
        return this.observedPairs.length;
    }

    // --- Path coverage (source -> destination endpoint) -----------------------
    // The headline coverage counts traffic PATHS - each a source reaching one destination address:port -
    // so it reflects the selection's real size, not just its distinct endpoints. The paths come from a
    // cheap restricted re-query of the selected traffic (the same query the coverage diagram uses, so the
    // two agree). Until it loads, or if it cannot run, the figure falls back to the coarse endpoint count.

    /** The re-queried flows behind the selection, or null while loading or if the query could not run. */
    private pathFlows: TrafficRow[] | null = null;
    /** True while the path re-query is in flight. */
    pathsLoading = false;
    /** Guards the one-shot path re-query so it runs once the network is known, not on every emission. */
    private pathsRequested = false;

    /** Memoized result of pathStats(), so the coverage getters that read it several times per
     *  change-detection pass do not each walk every flow. Recomputed when the flows or the cards change. */
    private pathStatsCache: { flows: TrafficRow[]; sig: string; value: { total: number; covered: number } } | null =
        null;

    /** Distinct source->endpoint paths and how many are covered by the current cards (source-aware). */
    private pathStats(): { total: number; covered: number } {
        const flows = this.pathFlows;
        if (!flows) return { total: 0, covered: 0 };
        const services = this.coverageServiceInputs();
        // The card list is small, so a serialized signature is cheap next to the per-flow walk below.
        const sig = JSON.stringify(services);
        const cache = this.pathStatsCache;
        if (cache && cache.flows === flows && cache.sig === sig) {
            return cache.value;
        }
        const seen = new Map<string, boolean>();
        for (const f of flows) {
            const address = (f.terminator_remote_address ?? '').trim();
            if (!address) continue;
            const port = `${f.terminator_remote_port ?? ''}`.trim();
            const source = `${(f.identity_name ?? '').trim()}|${(f.initiator_remote_ip ?? '').trim()}`;
            const key = `${source}=>${address}:${port}`;
            if (!seen.has(key)) {
                seen.set(key, isFlowCovered(f.initiator_remote_ip ?? '', f.identity_name ?? '', address, port, services));
            }
        }
        let covered = 0;
        seen.forEach((v) => (covered += v ? 1 : 0));
        const value = { total: seen.size, covered };
        this.pathStatsCache = { flows, sig, value };
        return value;
    }

    /** The coverage figure is ready once the re-query settles (to paths) or falls back to endpoints. */
    get coverageReady(): boolean {
        return !this.pathsLoading;
    }
    /** Numerator / denominator / unit for the Coverage line: paths once loaded, else the endpoint count. */
    get covNumerator(): number {
        return this.pathFlows ? this.pathStats().covered : this.coveredCount;
    }
    get covDenominator(): number {
        return this.pathFlows ? this.pathStats().total : this.totalSegments;
    }
    get coverageUnit(): string {
        return this.pathFlows ? 'paths' : 'endpoints';
    }
    get coveragePercent(): number {
        const total = this.covDenominator;
        return total ? Math.round((this.covNumerator / total) * 100) : 0;
    }

    /** The service cards as the coverage helpers read them (shared by the diagram and the path count). */
    private coverageServiceInputs(): CoverageServiceInput[] {
        return this.services.map((s) => ({
            name: s.name,
            accessMode: s.accessMode,
            addresses: [...s.addresses],
            ports: [...s.ports],
            allowedSourceAddresses: [...s.allowedSourceAddresses],
            dialAttributes: [...s.dialAttributes],
        }));
    }

    /** Re-query the selected traffic's flows (restricted to its dest IPs/ports) so coverage can count real
     *  source->endpoint paths. Degrades silently to the endpoint count when there is no window to query or
     *  the request fails. */
    private async loadPathCoverage(): Promise<void> {
        const qp = this.route.snapshot.queryParamMap;
        const destIp = qp.get('destIp') || '';
        const destPort = qp.get('destPort') || '';
        const start = Number(qp.get('start')) || undefined;
        const end = Number(qp.get('end')) || undefined;
        const network = this.apiService.currentNetwork?.value;
        if (!start || !end || !network?.id || !network?.networkGroupId || !(destIp || destPort)) {
            this.pathFlows = null; // nothing to query - keep the endpoint fallback
            return;
        }
        this.pathsLoading = true;
        try {
            const params = this.templateService.getTrafficAnalysisData(
                'ncfabriccircuits',
                moment(start).utc().format(),
                moment(end).utc().format(),
                undefined,
                undefined,
                network.id,
                network.networkGroupId,
                this.jsonArray(destIp),
                this.jsonArray(destPort),
                ''
            );
            const resp = await firstValueFrom(
                this.elasticsearch.apiTemplateSearch(this.templateService.TRAFFIC_ANALYSIS_DATA_TEMPLATE, params)
            );
            this.pathFlows = parseTrafficFlows(resp);
        } catch {
            this.pathFlows = null; // degrade to the endpoint count
        } finally {
            this.pathsLoading = false;
        }
    }

    /** A CSV of values as the traffic-analysis template's JSON-array filter param (e.g. '["a","b"]'). */
    private jsonArray(csv: string): string {
        const out = (csv || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .map((s) => `"${s}"`);
        return out.length ? `[${out.join(',')}]` : '';
    }

    /** Open the coverage flow-map modal, colored by how much of the selected traffic the current cards
     *  cover. Passes a snapshot of the cards and the selection so the diagram matches the Coverage bar. */
    openCoverage(): void {
        if (!this.totalSegments) return;
        const data: CoverageDialogData = {
            services: this.coverageServiceInputs(),
            pairs: this.observedPairs,
            destIp: this.route.snapshot.queryParamMap.get('destIp') || '',
            destPort: this.route.snapshot.queryParamMap.get('destPort') || '',
            srcIdentities: this.selectedSourceIdentities,
            srcIps: this.selectedSourceIps,
            hostIdentities: this.selectedHostIdentities,
            start: Number(this.route.snapshot.queryParamMap.get('start')) || undefined,
            end: Number(this.route.snapshot.queryParamMap.get('end')) || undefined,
            label: this.sourceLabel,
            coveredCount: this.coveredCount,
            totalSegments: this.totalSegments,
        };
        this.dialog
            .open(CoverageSankeyDialogComponent, {
                data,
                panelClass: 'cov-dialog-panel',
                hasBackdrop: false,
                maxWidth: 'none',
                autoFocus: false,
            })
            .afterClosed()
            .subscribe((edited?: CoverageDialogData['services']) => {
                if (!edited) return;
                // The modal returns the service snapshot with its include/exclude edits. Apply the edited
                // fields back to the matching card (same order as the snapshot), so the coverage bar and the
                // stored service config reflect them: source admission, and the service's endpoint targets.
                edited.forEach((snapshot, i) => {
                    const card = this.services[i];
                    if (!card) return;
                    card.allowedSourceAddresses = [...snapshot.allowedSourceAddresses];
                    card.dialAttributes = [...snapshot.dialAttributes];
                    card.addresses = [...snapshot.addresses];
                    card.ports = [...snapshot.ports];
                    // Keep the fixed-host defaults (fwd flags, address/port values) in step with the targets.
                    this.onAddressesChange(card);
                    this.onPortsChange(card);
                });
            });
    }

    /** Whether an observed address/port from the pool is referenced by at least one intercept service,
     *  i.e. it has been placed into a service. Coarser than per-segment coverage (an address pairs with
     *  several ports), so it marks "in use somewhere", while the Coverage bar remains the exact measure. */
    isAddressApplied(address: string): boolean {
        return this.services.some((s) => s.accessMode === 'address' && s.addresses.includes(address));
    }
    isPortApplied(port: string): boolean {
        return this.services.some((s) => s.accessMode === 'address' && s.ports.includes(port));
    }
    /** A selected source identity is applied while any service still grants it access (it defaults in,
     *  so it stays marked until the operator removes it from every service's "Accessible by"). */
    isSourceIdentityApplied(name: string): boolean {
        return this.services.some((s) => s.dialAttributes.includes(`@${name}`));
    }
    /** A selected host identity is applied while any service still lists it under "Hosted by". */
    isHostIdentityApplied(name: string): boolean {
        return this.services.some((s) => s.bindAttributes.includes(`@${name}`));
    }
    /** An observed source address is applied once a service restricts hosting to it (its "Allowed source
     *  addresses"); unlike the other pool values it is opt-in, so it starts unapplied. */
    isSourceAddressApplied(ip: string): boolean {
        return this.services.some((s) => s.allowedSourceAddresses.includes(ip));
    }

    // --- Pool chip rows: cap long lists so the reference panel stays compact (and sticky) ---------

    /** How many chips a pool row shows inline; a longer row shows the first `chipCap` and opens the rest
     *  in a popover, so the (sticky) panel never grows past this. */
    readonly chipCap = 10;
    /** Which pool row's "show all" popover is open (only one at a time). */
    popoverRow: string | null = null;

    /** The chips to render inline for a pool row: all of them when within the cap, else the first cap. */
    visibleChips(list: string[]): string[] {
        return list.length <= this.chipCap ? list : list.slice(0, this.chipCap);
    }
    toggleRowPopover(key: string, event: Event): void {
        event.stopPropagation();
        this.popoverRow = this.popoverRow === key ? null : key;
    }
    get totalResources(): number {
        return this.services.reduce((sum, s) => sum + this.resourceCount(s), 0);
    }
    private resourceCount(s: ServiceConfig): number {
        let n = 1;
        if (s.accessMode !== 'sdk') n += 1;
        if (s.hostMode !== 'sdk') n += 1;
        if (s.dialAttributes.length) n += 1;
        if (s.bindAttributes.length) n += 1;
        return n;
    }

    // --- Build + validate -----------------------------------------------------

    private interceptProtocols(s: ServiceConfig): string[] {
        return s.accessProtocol === 'both' ? ['tcp', 'udp'] : [s.accessProtocol];
    }

    private draftFor(s: ServiceConfig): MicrosegmentDraft {
        const addresses = s.addresses.map((a) => a.trim()).filter(Boolean);
        const ports = s.ports.map((p) => Number(p.trim())).filter((p) => Number.isFinite(p));
        const portRanges = collapsePortRanges(ports);
        const allowedPortRanges = collapsePortRanges(
            s.allowedPorts.map((p) => Number(p.trim())).filter((p) => Number.isFinite(p))
        );
        const host: HostSpec = {
            protocol: { forward: s.fwdProtocol, value: s.fwdProtocol ? undefined : s.protocolVal },
            address: { forward: s.fwdAddress, value: s.fwdAddress ? undefined : s.addressVal.trim() },
            port: { forward: s.fwdPort, value: s.fwdPort ? undefined : s.portVal.trim() },
            allowedProtocols: s.allowedProtocols.length ? [...s.allowedProtocols] : undefined,
            allowedAddresses: s.allowedAddresses.length ? s.allowedAddresses.map((a) => a.trim()) : undefined,
            allowedPortRanges: allowedPortRanges.length ? allowedPortRanges : undefined,
            allowedSourceAddresses: s.allowedSourceAddresses.length
                ? s.allowedSourceAddresses.map((a) => a.trim()).filter(Boolean)
                : undefined,
        };
        return {
            serviceName: s.name.trim(),
            addresses,
            portRanges,
            protocols: this.interceptProtocols(s),
            dialIdentityNames: [],
            bindIdentityNames: [],
            sourceServiceNames: [],
            circuits: 0,
            host,
        };
    }

    private validateService(s: ServiceConfig): boolean {
        if (s.name.trim().length === 0) return false;
        if (s.accessMode === 'address') {
            const addresses = s.addresses.map((a) => a.trim()).filter(Boolean);
            const ports = s.ports.map((p) => p.trim()).filter(Boolean);
            if (addresses.length === 0 || addresses.some((a) => !this.validationService.isValidInterceptHost(a))) {
                return false;
            }
            if (ports.length === 0 || ports.some((p) => !this.validationService.isValidPort(Number(p)))) {
                return false;
            }
        }
        if (s.hostMode === 'address') {
            if (!s.fwdAddress && s.addressVal.trim().length === 0) return false;
            if (!s.fwdPort && !this.validationService.isValidPort(Number(s.portVal))) return false;
        }
        return true;
    }

    get canCreate(): boolean {
        return (
            !this.creating &&
            !this.finished &&
            this.services.length > 0 &&
            this.services.every((s) => this.validateService(s))
        );
    }
    get createLabel(): string {
        if (this.isEdit) {
            return 'Save changes';
        }
        const n = this.services.length;
        return `Create ${n} service${n === 1 ? '' : 's'}`;
    }

    // --- Create ---------------------------------------------------------------

    async create(): Promise<void> {
        if (!this.canCreate) return;
        this.started = true;
        this.creating = true;
        this.hasErrors = false;
        this.services.forEach((s) => {
            // Collapsed by default: the live run shows open via the `creating` flag, and once finished each
            // card stays collapsed (its outcome line + status chip summarize it) until the user opens it.
            s.open = false;
            s.statuses = [];
            s.state = 'idle';
        });
        for (const service of this.services) {
            service.state = 'creating';
            try {
                const draft: SimpleServiceCreateDraft = { mode: 'intercept', draft: this.draftFor(service) };
                const input = {
                    draft,
                    sdkOnlyDial: service.accessMode === 'sdk',
                    sdkOnlyBind: service.hostMode === 'sdk',
                    serviceRoleAttributes: this.rolesOf(service.attributes),
                    dialNamedAttributes: this.namedOf(service.dialAttributes),
                    dialRoleAttributes: this.rolesOf(service.dialAttributes),
                    bindNamedAttributes: this.namedOf(service.bindAttributes),
                    bindRoleAttributes: this.rolesOf(service.bindAttributes),
                    encryptionRequired: true,
                    // Mark these as microsegmentation-workflow resources, so Traffic Analysis coverage
                    // counts only services created from traffic (not catch-alls or unrelated services).
                    tags: { [MICROSEGMENT_SOURCE_TAG_KEY]: MICROSEGMENT_SOURCE_TRAFFIC },
                };
                // In edit mode there is exactly one card: patch the existing microsegment in place.
                const { status$, result } =
                    this.isEdit && this.editExisting
                        ? await this.createService.update(this.editExisting, input)
                        : await this.createService.create(input);
                this.subscription.add(status$.subscribe((v) => (service.statuses = v)));
                const res = await result;
                service.state = res.hasErrors ? 'error' : 'done';
                if (res.hasErrors) this.hasErrors = true;
            } catch {
                service.state = 'error';
                this.hasErrors = true;
            }
        }
        this.creating = false;
        this.finished = true;
        // Keep every card expanded once the run finishes so the created steps stay visible
        // (the live run showed them open via `creating`; without this they collapse on save).
        this.services.forEach((s) => (s.open = true));
        if (!this.hasErrors) {
            const n = this.services.length;
            this.growlerService.show(
                this.isEdit
                    ? new GrowlerData('success', 'Success', 'Microsegment Updated', `"${this.services[0]?.name}" was updated.`)
                    : new GrowlerData('success', 'Success', 'Services Created', `${n} service${n === 1 ? '' : 's'} created.`)
            );
        }
    }

    // --- Navigation -----------------------------------------------------------

    get doneLabel(): string {
        if (this.isEdit) {
            return 'Back to Microsegments';
        }
        return this.fromTraffic ? 'Back to Traffic Analysis' : 'Go to Services';
    }
    /** The top-left back control returns to the previous step: the "Create a service" chooser. */
    get backLabel(): string {
        if (this.isEdit) {
            return 'Back to Microsegments';
        }
        return this.fromTraffic ? 'Back' : 'Go to Services';
    }
    back(): void {
        if (this.isEdit) {
            this.router.navigateByUrl(URLS.MICROSEGMENTS);
        } else if (this.fromTraffic) {
            // Return to the "Create a service" chooser (the step before this one), carrying the same
            // traffic selection so it re-opens on that selection rather than Traffic Analysis.
            this.router.navigate([URLS.SERVICE_SELECT], { queryParams: this.route.snapshot.queryParams });
        } else {
            this.router.navigateByUrl(ZITI_URLS.ZITI_SERVICES);
        }
    }
    done(): void {
        if (this.isEdit) {
            this.router.navigateByUrl(URLS.MICROSEGMENTS);
            return;
        }
        this.router.navigateByUrl(this.fromTraffic ? URLS.TRAFFIC_ANALYSIS : ZITI_URLS.ZITI_SERVICES);
    }
    /** Secondary action on the done screen: jump to the Services list to see what was created. */
    viewServices(): void {
        this.router.navigateByUrl(ZITI_URLS.ZITI_SERVICES);
    }

    /** Offer the per-source identity step only after a clean create, and only when the selection had
     *  source addresses to mint identities for. */
    get canCreateSourceIdentities(): boolean {
        return this.finished && !this.hasErrors && this.selectedSourceIps.length > 0;
    }

    /** Optional follow-up on the done screen: mint an identity per source address for the services just
     *  created. Carries the source addresses and the created service names to the identity step. */
    createSourceIdentities(): void {
        const svc = this.services
            .filter((s) => s.state === 'done')
            .map((s) => s.name)
            .filter(Boolean);
        this.router.navigate([URLS.SERVICE_SOURCE_IDENTITIES], {
            queryParams: { srcIp: this.selectedSourceIps.join(','), svc: svc.join(',') },
        });
    }

    // --- Creation summary -----------------------------------------------------

    /** The page title: an instruction while building, a success (or error) confirmation once finished. */
    get headerTitle(): string {
        if (this.isEdit) {
            if (!this.finished) return 'Edit microsegment';
            return this.hasErrors ? 'Updated with errors' : 'Microsegment updated';
        }
        if (!this.finished) return 'Create services from traffic';
        if (this.hasErrors) return 'Created with errors';
        return this.services.length > 1 ? 'Services created' : 'Service created';
    }
    get headerSubtitle(): string {
        if (this.isEdit) {
            if (!this.finished) return 'Change where clients reach this microsegment, how traffic is delivered, and who can connect.';
            if (this.hasErrors) return 'Some steps did not complete - open the service below to see what failed.';
            return 'The microsegment is now live with your changes.';
        }
        if (!this.finished) return 'Turn the selected traffic into one or more services.';
        if (this.hasErrors) return 'Some steps did not complete - open a service below to see what failed.';
        const n = this.services.length;
        return `${n === 1 ? 'The service is' : `All ${n} services are`} now live. Clients on the selected traffic can reach ${n === 1 ? 'it' : 'them'}.`;
    }

    /** The status chip text on a service card during / after creation. */
    statusLabel(s: ServiceConfig): string {
        return s.state === 'done'
            ? this.isEdit
                ? 'Updated'
                : 'Created'
            : s.state === 'error'
            ? 'Had errors'
            : s.state === 'creating'
            ? this.isEdit
                ? 'Saving...'
                : 'Creating...'
            : 'Waiting';
    }

    /** The one-line outcome under a service's name: what was created, or the live / error state. */
    outcomeLine(s: ServiceConfig): string {
        if (s.state === 'creating') return this.isEdit ? 'Saving service and resources...' : 'Creating service and resources...';
        if (s.state === 'error') return 'Some steps did not complete';
        if (s.state === 'done') {
            const { configs, policies } = this.summaryCounts(s);
            const parts = ['Service'];
            if (configs) parts.push(`${configs} configuration${configs === 1 ? '' : 's'}`);
            if (policies) parts.push(`${policies} polic${policies === 1 ? 'y' : 'ies'}`);
            return `${this.joinAnd(parts)} ${this.isEdit ? 'updated' : 'created'}`;
        }
        return 'Waiting to start';
    }

    /** Count the configurations and policies created for a service, from its step list. */
    private summaryCounts(s: ServiceConfig): { configs: number; policies: number } {
        let configs = 0;
        let policies = 0;
        (s.statuses ?? []).forEach((st) => {
            if (/policy/i.test(st.step)) policies += 1;
            else if (/^Configuration "/.test(st.step)) configs += 1;
        });
        return { configs, policies };
    }

    /** Join phrases as "a", "a and b", or "a, b and c". */
    private joinAnd(parts: string[]): string {
        if (parts.length <= 1) return parts[0] ?? '';
        return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    }

    // --- Helpers --------------------------------------------------------------

    private splitParam(value: string | null): string[] {
        return (value || '')
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean);
    }
    private distinct(values: string[]): string[] {
        return Array.from(new Set(values));
    }
}
